import { Suspense } from 'react';
import { Await, Link, PathMatch } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { AutoSizer } from 'react-virtualized';
import { fromBase64 } from 'js-base64';
import { enums } from '@kattana-io/common';
import Text from '../../atoms/Text';
import Image from '../../atoms/Image';
import NewPairsTable from './NewPairsTable';
import Slider from '../../molecules/Slider';
import ChainSelector from '../../components/ChainSelector';
import { newPairsPageLoaderPDataType } from '../loaders/newPairsLoader';
import backIcon from '../../assets/images/arrow_left.svg';
import NewPairsTablePlaceholder from './Placeholder';
import Fallback from '../../components/Fallback';
import TableFilter, {
  TableFilterOption,
  TableFilterSelect,
} from '../../components/TableComponents/Filter';
import { IRates } from '../../reducers/rates';

import './styles.scss';

interface INewPairsBodyProps {
  tickers: unknown;
  rates: IRates;
  chain: string;
  page: string;
  plan: enums.PricingPlan;
  liquidityFilterOptions: TableFilterOption[];
  liquidity?: string | null;
  loading: false | PathMatch<string> | null;
  sort: string;
  onSetChain(chain: string): void;
  onSetPage(page: number): void;
  onLiquidityFilterSelect: TableFilterSelect;
  handleSort(id: string): void;
}

const NewPairsBody = ({
  tickers,
  rates,
  chain,
  page,
  plan,
  liquidityFilterOptions,
  liquidity,
  loading,
  sort,
  onSetChain,
  onSetPage,
  onLiquidityFilterSelect,
  handleSort,
}: INewPairsBodyProps) => {
  return (
    <div className='newPairsPage__container'>
      <div className='newPairsPage__container__line'>
        <Link to='/'>
          <Image src={backIcon} className='back__btn' size='l' />
        </Link>
        <Text size={18} className='title text--grey-200'>
          New pairs
        </Text>
      </div>
      <div className='newPairsPage__header'>
        <ChainSelector selected={chain} onSet={onSetChain} />
        {plan === enums.PricingPlan.premium && (
          <TableFilter
            options={liquidityFilterOptions}
            selected={liquidity}
            title='Liquidity'
            onSelect={onLiquidityFilterSelect}
          />
        )}
      </div>
      <Suspense fallback={<NewPairsTablePlaceholder />}>
        <Await resolve={tickers} errorElement={<div>Error Loading</div>}>
          {(data: newPairsPageLoaderPDataType) => (
            <ErrorBoundary
              fallbackRender={(props) => (
                <Fallback
                  {...props}
                  className='newPairs__table'
                  title='New Pairs Table'
                />
              )}
              onReset={(details) => {
                // Reset the state of your app so the error doesn't happen again
              }}
            >
              <AutoSizer style={{ width: '100%', height: '100%' }}>
                {({ width }) => (
                  <NewPairsTable
                    pairs={data || []}
                    rates={rates}
                    tableSort={JSON.parse(fromBase64(sort))}
                    width={width}
                    loading={!!loading}
                    handleSort={handleSort}
                  />
                )}
              </AutoSizer>
              <Slider
                page={Number(page)}
                isFinal={data?.length ? data.length % 100 !== 0 : true}
                setPage={onSetPage}
              />
            </ErrorBoundary>
          )}
        </Await>
      </Suspense>
    </div>
  );
};

export default NewPairsBody;
